<template>
  <div
    class="text-brand-text relative dark:text-white"
    :class="[
      immersive ? 'flex flex-col' : 'ring-surface-500/40 rounded p-3 ring-1',
      disabled
        ? 'cursor-not-allowed bg-pink-50 opacity-75 dark:bg-pink-900/20'
        : immersive
          ? ''
          : 'bg-white dark:bg-slate-700'
    ]">
    <!-- Liquid Variables Picker Button (only show in Flow context with available variables) -->
    <div
      v-if="isInFlowContext && hasAnyVariables && !disabled"
      class="ring-surface-500/40 text-surface-900 hover:text-surface-700 dark:text-surface-200/70 dark:hover:text-surface-200 absolute -top-2.5 right-2 flex cursor-pointer items-center rounded-md bg-white px-2 py-0.5 text-xs ring-1 dark:bg-slate-700"
      @click="showLiquidVariablesPicker = true">
      <PlusIcon class="h-4 w-4" />
    </div>

    <div
      v-if="liquidCombinations.length && !disabled"
      class="ring-surface-500/40 text-surface-900 hover:text-surface-700 dark:text-surface-200/70 dark:hover:text-surface-200 absolute -top-2.5 cursor-pointer rounded-md bg-white px-2 py-0.5 text-xs ring-1 dark:bg-slate-700"
      :class="isInFlowContext ? 'right-14' : 'right-2'"
      @click="showPreviewPersonalizations = true">
      Preview variations ({{ liquidCombinations.length }})
    </div>

    <Dialog
      v-model:visible="showPreviewPersonalizations"
      modal
      :header="`${liquidCombinations.length} possible variations`"
      class="max-w-4xl">
      <ScrollPanel style="width: 100%; height: 400px">
        <ul class="space-y-8">
          <li v-for="combination in liquidCombinations" :key="combination" class="space-y-3">
            <div class="border-b pb-8">
              <div class="flex space-x-4 text-sm">
                <div
                  v-for="segment in combination"
                  :key="segment.segmentGroupId"
                  class="bg-primary-700/10 dark:bg-primary-300/10 rounded px-4 py-2.5">
                  <div class="text-xs font-semibold">
                    {{ segmentGroupName(segment.segmentGroupId) }}
                  </div>
                  <div>
                    <template v-if="segment.segment">
                      {{ stripHtml(segmentName(segmentGroupStore.segmentForSegmentId(segment.segment))) }}
                    </template>
                    <template v-else>
                      <span class="opacity-50">Unknown / Not set</span>
                    </template>
                  </div>
                </div>
              </div>
              <div class="rendered-email mt-8" v-html="previewContent(modelValue, combination)" />
            </div>
          </li>
        </ul>
      </ScrollPanel>
    </Dialog>

    <FloatingMenu
      v-if="editor && multiline && !disabled"
      :editor="editor"
      :tippy-options="{ duration: 100, inertia: true }">
      <div
        class="bg-primary-800 flex space-x-4 rounded px-4 py-2 text-white shadow"
        data-rich-text-editor-toolbar="floating"
        @mousedown.prevent>
        <button
          class="flex hover:text-yellow-500"
          :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }"
          @click="editor.chain().focus().toggleHeading({ level: 1 }).run()">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M14 4.25a.75.75 0 0 0-1.248-.56l-2.25 2a.75.75 0 0 0 .996 1.12l1.002-.89v5.83a.75.75 0 0 0 1.5 0zm-11.5 0a.75.75 0 0 0-1.5 0v7.496a.75.75 0 0 0 1.5 0V8.75h4v2.996a.75.75 0 0 0 1.5 0V4.25a.75.75 0 0 0-1.5 0v3h-4z"
              clip-rule="evenodd" />
          </svg>
        </button>
        <button
          class="flex hover:text-yellow-500"
          :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
          @click="editor.chain().focus().toggleHeading({ level: 2 }).run()">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M2.5 4.25a.75.75 0 0 0-1.5 0v7.496a.75.75 0 0 0 1.5 0V8.75h4v2.996a.75.75 0 0 0 1.5 0V4.25a.75.75 0 0 0-1.5 0v3h-4zm8.403 1.783A1.364 1.364 0 0 1 12.226 5h.226a1.071 1.071 0 0 1 .672 1.906l-3.61 2.906a1.51 1.51 0 0 0 .947 2.688h3.789a.75.75 0 0 0 0-1.5h-3.793l-.003-.003l-.003-.004v-.004a.01.01 0 0 1 .004-.008l3.61-2.907A2.571 2.571 0 0 0 12.452 3.5h-.226c-1.314 0-2.46.894-2.778 2.17l-.038.148a.75.75 0 1 0 1.456.364z"
              clip-rule="evenodd" />
          </svg>
        </button>
        <button
          class="flex hover:text-yellow-500"
          :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
          @click="editor.chain().focus().toggleHeading({ level: 3 }).run()">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M2.5 4.25a.75.75 0 0 0-1.5 0v7.496a.75.75 0 0 0 1.5 0V8.75h4v2.996a.75.75 0 0 0 1.5 0V4.25a.75.75 0 0 0-1.5 0v3h-4zm8.114 1.496c.202-.504.69-.834 1.232-.834h.28a.94.94 0 0 1 .929.796l.027.18a1.15 1.15 0 0 1-.911 1.303l-.8.16a.662.662 0 0 0 .129 1.31h1.21a.89.89 0 0 1 .882 1.017a1.67 1.67 0 0 1-1.414 1.414l-.103.015a1.81 1.81 0 0 1-1.828-.9l-.018-.033a.662.662 0 0 0-1.152.652l.018.032a3.13 3.13 0 0 0 3.167 1.559l.103-.015a2.99 2.99 0 0 0 2.537-2.537a2.21 2.21 0 0 0-1.058-2.216a2.47 2.47 0 0 0 .547-1.963l-.028-.179a2.26 2.26 0 0 0-2.237-1.919h-.28a2.65 2.65 0 0 0-2.46 1.666a.662.662 0 1 0 1.228.492"
              clip-rule="evenodd" />
          </svg>
        </button>
        <button
          class="flex hover:text-yellow-500"
          :class="{ 'is-active': editor.isActive('heading', { level: 4 }) }"
          @click="editor.chain().focus().toggleHeading({ level: 4 }).run()">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M14.25 4.25a.75.75 0 0 0-1.347-.454l-4 5.25A.75.75 0 0 0 9.5 10.25h3.25v1.496a.75.75 0 0 0 1.5 0V10.25h.25a.75.75 0 1 0 0-1.5h-.25zm-1.5 2.222V8.75h-1.736zM2.5 4.25a.75.75 0 1 0-1.5 0v7.496a.75.75 0 0 0 1.5 0V8.75h4v2.996a.75.75 0 0 0 1.5 0V4.25a.75.75 0 1 0-1.5 0v3h-4z"
              clip-rule="evenodd" />
          </svg>
        </button>
        <button
          class="flex hover:text-yellow-500"
          :class="{ 'is-active': editor.isActive('bulletList') }"
          @click="editor.chain().focus().toggleBulletList().run()">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M2.5 5a1.25 1.25 0 1 0 0-2.5a1.25 1.25 0 0 0 0 2.5m3.25-2a.75.75 0 0 0 0 1.5h8.5a.75.75 0 0 0 0-1.5zm0 8.5a.75.75 0 0 0 0 1.5h8.5a.75.75 0 0 0 0-1.5zM5 8a.75.75 0 0 1 .75-.75h8.5a.75.75 0 0 1 0 1.5h-8.5A.75.75 0 0 1 5 8M3.75 8a1.25 1.25 0 1 1-2.5 0a1.25 1.25 0 0 1 2.5 0M2.5 13.5a1.25 1.25 0 1 0 0-2.5a1.25 1.25 0 0 0 0 2.5"
              clip-rule="evenodd" />
          </svg>
        </button>
        <button
          class="flex hover:text-yellow-500"
          :class="{ 'is-active': editor.isActive('orderedList') }"
          @click="editor.chain().focus().toggleOrderedList().run()">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M3.21 2.5a.5.5 0 0 0-.796-.403l-.71.524a.5.5 0 0 0 .507.856V5a.5.5 0 0 0 1 0zm2.54.5a.75.75 0 1 0 0 1.5h8.5a.75.75 0 0 0 0-1.5zm0 8.5a.75.75 0 0 0 0 1.5h8.5a.75.75 0 0 0 0-1.5zM5 8a.75.75 0 0 1 .75-.75h8.5a.75.75 0 0 1 0 1.5h-8.5A.75.75 0 0 1 5 8m-2.596-.67c-.07.051-.087.097-.09.116a.5.5 0 0 1-.988-.149c.051-.345.26-.61.495-.779c.236-.169.531-.268.83-.268c.345 0 .666.109.902.343s.337.543.337.85c0 .51-.321.855-.76 1.278l-.02.018l-.013.011h.493a.5.5 0 0 1 0 1H1.75a.5.5 0 0 1-.326-.879l1.022-.88c.223-.216.338-.344.398-.434c.046-.069.046-.092.046-.111v-.002c0-.09-.027-.127-.041-.141c-.013-.013-.062-.053-.199-.053a.44.44 0 0 0-.246.08M1.9 10.5a.5.5 0 0 0 0 1h.38l-.15.18a.5.5 0 0 0 .383.82c.259 0 .365.077.407.122c.051.054.08.133.08.218a.02.02 0 0 1-.004.014a.2.2 0 0 1-.057.052a.7.7 0 0 1-.371.094a.55.55 0 0 1-.258-.057c-.059-.032-.075-.061-.076-.064a.5.5 0 0 0-.91.416c.213.464.728.705 1.244.705c.319 0 .651-.082.92-.258c.275-.181.512-.487.512-.902c0-.293-.096-.634-.355-.906a1.3 1.3 0 0 0-.252-.206l.34-.407a.5.5 0 0 0-.383-.821z"
              clip-rule="evenodd" />
          </svg>
        </button>
      </div>
    </FloatingMenu>

    <BubbleMenu
      v-if="editor && !disabled"
      :editor="editor"
      :tippy-options="{ duration: 100 }"
      :class="{ hidden: personalizeDialogVisible || conditionalEditorVisible }">
      <div
        class="bg-primary-800 flex flex-wrap items-center space-x-4 rounded px-4 py-2 text-white shadow"
        data-rich-text-editor-toolbar="bubble"
        @mousedown.prevent>
        <button
          class="flex hover:text-yellow-500"
          :class="{ 'text-yellow-300': editor.isActive('bold') }"
          @click="editor.chain().focus().toggleBold().run()">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M4.25 2.25A.75.75 0 0 0 3.5 3v10c0 .414.336.75.75.75H9.5a3.25 3.25 0 0 0 1.477-6.146A3.25 3.25 0 0 0 8.5 2.25zm3.5 5a1.75 1.75 0 1 0 0-3.5h-2v3.5zm-2 1.5v3.5h3a1.75 1.75 0 1 0 0-3.5z"
              clip-rule="evenodd" />
          </svg>
        </button>
        <button
          class="flex hover:text-yellow-500"
          :class="{ 'text-yellow-300': editor.isActive('italic') }"
          @click="editor.chain().focus().toggleItalic().run()">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M7.25 2a.75.75 0 0 0 0 1.5h1.317l-2.7 9H4.25a.75.75 0 1 0 0 1.5h4.5a.75.75 0 0 0 0-1.5H7.433l2.7-9h1.617a.75.75 0 0 0 0-1.5z"
              clip-rule="evenodd" />
          </svg>
        </button>
        <button
          class="flex hover:text-yellow-500"
          :class="{ 'text-yellow-300': editor.isActive('strike') }"
          @click="editor.chain().focus().toggleStrike().run()">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M5.502 2.757C6.214 2.236 7.122 2 7.984 2c1.685 0 3.015.572 3.687 1.915a.75.75 0 1 1-1.342.67C10.001 3.93 9.331 3.5 7.984 3.5c-.611 0-1.19.17-1.597.468c-.384.28-.627.678-.627 1.242c0 .403.165.758.463 1.04H4.447a2.9 2.9 0 0 1-.187-1.04c0-1.084.507-1.916 1.242-2.453m6.047 5.993h1.201a.75.75 0 0 0 0-1.5h-9.5a.75.75 0 0 0 0 1.5h5.475l.043.012h.002c1.196.323 1.98 1.005 1.98 1.988c0 .669-.289 1.063-.742 1.33c-.5.296-1.222.437-2 .437c-1.398 0-2.453-.472-2.796-1.504a.75.75 0 1 0-1.424.474c.657 1.969 2.602 2.53 4.22 2.53c.915 0 1.94-.16 2.762-.644c.869-.512 1.48-1.376 1.48-2.623c0-.822-.276-1.481-.7-2"
              clip-rule="evenodd" />
          </svg>
        </button>
        <button
          class="flex hover:text-yellow-500"
          :class="{ 'text-yellow-300': editor.isActive('underline') }"
          @click="editor.chain().focus().toggleUnderline().run()">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M5 2.75a.75.75 0 0 0-1.5 0V7a4.5 4.5 0 0 0 9 0V2.75a.75.75 0 0 0-1.5 0V7a3 3 0 0 1-6 0zm-.75 9.75a.75.75 0 0 0 0 1.5h7.5a.75.75 0 0 0 0-1.5z"
              clip-rule="evenodd" />
          </svg>
        </button>
        <button
          class="flex hover:text-yellow-500"
          :class="{ 'text-yellow-300': editor.isActive('link') }"
          @click="setLink">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M3.47 6.53a.75.75 0 0 1 1.06 1.061l-.727.727a2.743 2.743 0 0 0 3.879 3.879l.727-.727a.75.75 0 0 1 1.06 1.06l-.726.727a4.243 4.243 0 0 1-6-6zm8 1.879a.75.75 0 0 0 1.06 1.06l.727-.726a4.243 4.243 0 0 0-6-6l-.727.727a.75.75 0 0 0 1.061 1.06l.727-.727a2.743 2.743 0 0 1 3.879 3.879zm-.94-1.879a.75.75 0 1 0-1.06-1.06l-4 4a.75.75 0 1 0 1.06 1.06z"
              clip-rule="evenodd" />
          </svg>
        </button>
        <button
          v-if="canPersonalize && !liquidConditions"
          class="flex items-center space-x-1 hover:text-yellow-500"
          @click="promptPersonalizer()">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
            <g clip-path="url(#rmPersonGear)">
              <path
                fill-rule="evenodd"
                d="M7 6.5a2 2 0 1 0 0-4a2 2 0 0 0 0 4M7 8a3.5 3.5 0 1 0 0-7a3.5 3.5 0 0 0 0 7m-.5 1.299c0-.43-.36-.774-.785-.724C2.473 8.955 0 10.728 0 12.5A2.5 2.5 0 0 0 2.5 15h3.25a.75.75 0 0 0 0-1.5H2.5a1 1 0 0 1-1-1c0-.205.22-.809 1.32-1.459c.765-.452 1.792-.813 2.969-.964c.397-.051.711-.378.711-.778m8.002 1.89a2.22 2.22 0 0 1-1.66-2.138l-.68-.256a2.38 2.38 0 0 1-2.747.816l-.406.569c.57.788.57 1.87 0 2.657l.406.569a2.38 2.38 0 0 1 2.747.816l.68-.255a2.22 2.22 0 0 1 1.66-2.139zm-.112-2.396a.715.715 0 0 0-.418-.921l-1.798-.674a.865.865 0 0 0-1.114.506a.87.87 0 0 1-1.32.395a.884.884 0 0 0-1.23.2L7.434 9.805a.77.77 0 0 0 .179 1.076c.43.308.43.948 0 1.255a.77.77 0 0 0-.18 1.076l1.077 1.506a.884.884 0 0 0 1.23.2a.87.87 0 0 1 1.32.395a.865.865 0 0 0 1.114.506l1.798-.674a.715.715 0 0 0 .418-.92a.715.715 0 0 1 .67-.966h.134a.81.81 0 0 0 .809-.809v-1.883a.81.81 0 0 0-.809-.808h-.135a.715.715 0 0 1-.669-.966m-1.833 2.659a1.052 1.052 0 1 1-2.103 0a1.052 1.052 0 0 1 2.103 0"
                clip-rule="evenodd" />
            </g>
            <defs>
              <clipPath id="rmPersonGear"><path d="M0 0h16v16H0z" /></clipPath>
            </defs>
          </svg>
          <div class="text-xs">Personalize</div>
        </button>
        <button
          v-if="liquidConditions"
          class="flex items-center space-x-1 hover:text-yellow-500"
          @click="promptConditional()">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M7.5 5a1.5 1.5 0 1 1-3 0a1.5 1.5 0 0 1 3 0m1.405.75a3.001 3.001 0 0 1-5.81 0H1.747a.75.75 0 0 1 0-1.5h1.348a3.001 3.001 0 0 1 5.81 0h5.345a.75.75 0 0 1 0 1.5zm-7.158 4.5h5.345a3.001 3.001 0 0 1 5.811 0h1.347a.75.75 0 1 1 0 1.5h-1.347a3.001 3.001 0 0 1-5.81 0H1.746a.75.75 0 0 1 0-1.5m8.25-.75a1.5 1.5 0 1 0 0 3a1.5 1.5 0 0 0 0-3"
              clip-rule="evenodd" />
          </svg>
          <div class="text-xs">Conditional</div>
        </button>
        <button v-if="liquidConditions" class="flex items-center space-x-1 hover:text-yellow-500" @click="promptAi()">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="m7.836 6l.76-2.027L9.336 2H5.5a.72.72 0 0 0-.664.45L2.513 8.257a.177.177 0 0 0 .164.243h4.965l-.732 2.013l-1.082 2.975a.382.382 0 0 0 .637.392l6.956-7.391A.29.29 0 0 0 13.21 6H7.835m2.727-3l.235-.627A1.386 1.386 0 0 0 9.5.5h-4c-.906 0-1.72.552-2.057 1.393L1.12 7.7A1.677 1.677 0 0 0 2.677 10H5.5l-.545 1.5l-.537 1.475a1.882 1.882 0 0 0 3.14 1.933l6.956-7.391A1.79 1.79 0 0 0 13.21 4.5H10z"
              clip-rule="evenodd" />
          </svg>
          <div class="text-xs">AI</div>
        </button>
      </div>
    </BubbleMenu>
    <div :class="immersive ? 'flex flex-1 flex-col' : ''">
      <EditorContent :editor="editor" :class="immersive ? 'flex-1 [&>.tiptap]:h-full' : ''" />
    </div>

    <Phrase
      :visible="personalizeDialogVisible"
      :phrase-id="null"
      :fallback-text="selectedText"
      :surrounding-content="modelValue"
      @update:visible="personalizeDialogVisible = $event"
      @save="insertNewPersonalizationPhrase" />

    <ConditionalEditor
      v-if="liquidConditions"
      :visible="conditionalEditorVisible"
      :fallback-text="selectedText"
      :conditional-id="selectedConditional?.conditionalId"
      :existing-branches="selectedConditional?.branches"
      :existing-conditions="selectedConditional?.conditions"
      :existing-else-content="selectedConditional?.elseContent"
      @update:visible="conditionalEditorVisible = $event"
      @save="insertNewConditional"
      @remove="removeConditional" />

    <AiPersonalizeModal
      :visible="aiPersonalizeVisible"
      :selected-text="selectedText"
      :surrounding-content="editor?.getHTML() || ''"
      @update:visible="aiPersonalizeVisible = $event"
      @personalized="insertPersonalizedContent" />

    <LiquidVariablesPicker
      :visible="showLiquidVariablesPicker"
      @update:visible="showLiquidVariablesPicker = $event"
      @add="insertLiquidVariable" />
  </div>
</template>

<script setup>
import { useEditor, EditorContent, BubbleMenu, FloatingMenu } from '@tiptap/vue-3'
import { getHTMLFromFragment } from '@tiptap/vue-3'
import { DOMSerializer } from 'prosemirror-model'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import PersonalizerExtension from './tiptap/personalizerExtension'
import LiquidConditionalExtension from './tiptap/liquidConditionalExtension.js'
import { uuid } from '@/services/uuid'
import { useDebounceFn } from '@vueuse/core'
import { stripHtml } from '@/services/string'
import { conditionalHtmlToLiquid, liquidConditionalsToHtml } from './liquidConditionals'
import ConditionalEditor from './ConditionalEditor.vue'
import AiPersonalizeModal from './AiPersonalizeModal.vue'
import LiquidVariablesPicker from './LiquidVariablesPicker.vue'
import { PlusIcon } from '@heroicons/vue/24/outline'
import { useFlowLiquidVariables } from '@/modules/flow/composables/useFlowLiquidVariables'

const integrationStore = useIntegrationStore()
const { integratedPartner } = storeToRefs(integrationStore)

const emit = defineEmits(['update:modelValue', 'blur'])
const props = defineProps({
  modelValue: {
    type: String,
    required: false
  },
  multiline: {
    type: Boolean,
    default: false
  },
  canPersonalize: {
    type: Boolean,
    default: true
  },
  editorClasses: {
    type: String
  },
  liquidConditions: {
    type: Boolean,
    default: false
  },
  raw: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  immersive: {
    type: Boolean,
    default: false
  }
})

const phraseStore = usePhraseStore()
const { phrases } = storeToRefs(phraseStore)

const segmentGroupStore = useSegmentGroupStore()
const { segmentGroups } = storeToRefs(segmentGroupStore)

/* Previewing */

const showPreviewPersonalizations = ref(false)

// Maximum number of combinations to generate before giving up
// This prevents stack overflow and browser freezing with complex personalizations
const MAX_COMBINATIONS = 500

const generateCombinations = (segmentGroups) => {
  const groupEntries = Object.entries(segmentGroups)
  const groups = groupEntries.map(([id, set]) => ({
    id,
    segments: [...Array.from(set), null]
  }))

  // Calculate total combinations upfront to avoid stack overflow
  const totalCombinations = groups.reduce((acc, group) => acc * group.segments.length, 1)

  // If combinations would exceed our limit, return empty to disable preview
  if (totalCombinations > MAX_COMBINATIONS) {
    return []
  }

  // Use iterative approach instead of recursive to avoid stack overflow
  let combinations = [[]]

  for (const group of groups) {
    const newCombinations = []
    for (const combination of combinations) {
      for (const segment of group.segments) {
        newCombinations.push([
          ...combination,
          {
            segmentGroupId: group.id,
            segment: segment
          }
        ])
      }
    }
    combinations = newCombinations
  }

  return combinations
}

const segmentGroupName = (id) => {
  const group = segmentGroups.value.find((sg) => sg.id === id)
  return group ? group.name : id
}

const liquidCombinations = computed(() => {
  if (!props.modelValue?.includes('{{ phrases.')) {
    return []
  }

  // Extract all phrase IDs from the liquid
  const phraseIds = props.modelValue.match(/{{ phrases\.(\w+) }}/g).map((m) => {
    return m.match(/{{ phrases\.(\w+) }}/)[1]
  })

  const includedPhrases = phrases.value.filter((p) => phraseIds.includes(p.id))

  const includedSegmentGroups = includedPhrases.map((phrase) => {
    return segmentGroups.value.find((sg) => sg.id === phrase.segmentGroupId)
  })

  const groupings = includedSegmentGroups.reduce((acc, sg) => {
    if (!sg) return acc
    if (!acc[sg.id]) {
      acc[sg.id] = new Set()
    }
    return acc
  }, {})

  includedPhrases.forEach((phrase) => {
    Object.keys(phrase.map).forEach((key) => {
      if (!groupings[phrase.segmentGroupId]) {
        groupings[phrase.segmentGroupId] = new Set()
      }
      groupings[phrase.segmentGroupId].add(key)
    })
  })

  return generateCombinations(groupings)
})

const previewContent = (liquid, combination) => {
  const liquidWithPhrases = liquid.replace(/{{ phrases\.(\w+) }}/g, (match, id) => {
    const phrase = phrases.value.find((p) => p.id === id)

    if (phrase) {
      const mapping = phrase.map[combination.find((c) => c.segmentGroupId === phrase.segmentGroupId).segment]

      if (mapping) {
        return `<span class="border-b-2 border-yellow-300">${mapping}</span>`
      } else {
        return `<span class="border-b-2 border-sky-300/50">${phrase.fallbackText}</span>`
      }
    }

    return match
  })

  return liquidWithPhrases
}

/**
 * Get properly serialized HTML from the editor using ProseMirror's DOMSerializer
 * This uses the schema's renderHTML methods instead of capturing the NodeView DOM,
 * which ensures we get clean HTML without Vue component wrapper elements
 */
const getSerializedHTML = (editorInstance) => {
  if (!editorInstance) return ''

  const { state } = editorInstance
  const serializer = DOMSerializer.fromSchema(state.schema)
  const fragment = serializer.serializeFragment(state.doc.content)

  // Convert the document fragment to HTML string
  const div = document.createElement('div')
  div.appendChild(fragment)
  return div.innerHTML
}

const htmlToLiquid = (html = '') => {
  // First handle phrases
  const withPhraseSpans = html.replace(/{{\s*phrases\.(\w+)\s*}}/g, (match, id) => {
    const phrase = phrases.value.find((p) => p.id === id)
    return phrase ? `<span data-phrase-id="${id}">${phrase.fallbackText}</span>` : match
  })

  return conditionalHtmlToLiquid(withPhraseSpans)
}

const liquidToHtml = (liquid = '') => {
  // Handle null/undefined gracefully
  if (!liquid) return ''

  // First handle phrases
  let result = liquid.replace(/{{\s*phrases\.(\w+)\s*}}/g, (match, id) => {
    const phrase = phrases.value.find((p) => p.id === id)
    return phrase ? `<span data-phrase-id="${id}">${phrase.fallbackText}</span>` : match
  })

  result = liquidConditionalsToHtml(result, uuid)

  // If the content doesn't start with a paragraph tag but isn't empty, wrap it in a paragraph
  // This ensures TipTap has proper content to work with
  if (
    result &&
    !result.startsWith('<p>') &&
    !result.startsWith('<h') &&
    !result.startsWith('<ul') &&
    !result.startsWith('<ol') &&
    !result.startsWith('<div')
  ) {
    result = `<p>${result}</p>`
  }

  return result
}

const debouncedEmit = useDebounceFn((value) => {
  emit('update:modelValue', value)
}, 300) // 300ms debounce time, adjust as needed

const richTextEditorToolbarSelector = '[data-rich-text-editor-toolbar]'

const isRichTextEditorToolbarElement = (target) => {
  return target instanceof Element && !!target.closest(richTextEditorToolbarSelector)
}

const editor = useEditor({
  content: props.raw ? props.modelValue : liquidToHtml(props.modelValue),
  editable: !props.disabled,
  editorProps: {
    attributes: {
      class: `focus:outline-none ${props.editorClasses}`
    },
    // Handle paste events to auto-convert raw Liquid to visual tokens
    handlePaste: (view, event, slice) => {
      // Only process in liquid conditions mode
      if (!props.liquidConditions || props.raw) {
        return false // Let default handling proceed
      }

      // Get the plain text content from clipboard
      const clipboardText = event.clipboardData?.getData('text/plain') || ''

      // Check if the pasted content contains Liquid conditional syntax
      const hasLiquidConditional = /{%-?\s*(if|elsif|else|endif)\b/.test(clipboardText)

      if (hasLiquidConditional) {
        // Prevent the default paste
        event.preventDefault()

        // Convert the Liquid syntax to visual token HTML
        const convertedHtml = liquidToHtml(clipboardText)

        // Use setTimeout to ensure editor is ready and insert content properly
        setTimeout(() => {
          if (editor.value) {
            editor.value
              .chain()
              .focus()
              .insertContent(convertedHtml, {
                parseOptions: { preserveWhitespace: 'full' }
              })
              .run()
          }
        }, 0)

        return true // Indicate we handled the paste
      }

      return false // Let default handling proceed for non-Liquid content
    }
  },
  extensions: [
    StarterKit.configure({ history: false }),
    Link.configure({ openOnClick: true, autolink: false }),
    Underline,
    PersonalizerExtension,
    LiquidConditionalExtension
  ],
  onUpdate({ editor }) {
    // Use getSerializedHTML to get clean HTML from ProseMirror's DOMSerializer
    // This uses the schema's renderHTML methods instead of the NodeView DOM,
    // which avoids capturing Vue component wrapper elements
    const serializedHtml = getSerializedHTML(editor)
    const html = props.raw ? serializedHtml : htmlToLiquid(serializedHtml)
    // If the content is empty or just a single empty paragraph, emit an empty string
    const sanitized = html === '<p></p>' || html === '' ? '' : html
    debouncedEmit(sanitized)
  },
  onBlur({ event }) {
    if (isRichTextEditorToolbarElement(event?.relatedTarget)) {
      return
    }
    // Reconcile editor content with props.modelValue on blur. The focus guard
    // in the modelValue watcher skips setContent() while the user types, so if
    // an external merge changed the same field mid-edit, the editor and store
    // can diverge. Re-syncing on blur ensures the user sees what will actually
    // be saved, without interrupting active typing. (ENG-966)
    if (editor.value) {
      const currentHtml = getSerializedHTML(editor.value)
      const currentLiquid = props.raw ? currentHtml : htmlToLiquid(currentHtml)
      if (props.modelValue !== currentLiquid) {
        editor.value.commands.setContent(props.raw ? props.modelValue : liquidToHtml(props.modelValue))
      }
    }
    emit('blur')
  },
  onCreate({ editor }) {
    // Listen for editConditional events from the ConditionalToken component
    editor.on('editConditional', ({ conditionalId, branches, conditions, elseContent, elseHtml }) => {
      // Set the selected conditional data
      // Prefer explicit elseHtml when available; fall back to elseContent
      selectedConditional.value = { conditionalId, branches, conditions, elseContent: elseHtml || elseContent }
      // Open the conditional editor
      conditionalEditorVisible.value = true
    })
  }
})

const setLink = () => {
  const previousUrl = editor.value.getAttributes('link').href
  const url = window.prompt('URL', previousUrl)

  // cancelled
  if (url === null) {
    return
  }

  // empty
  if (url === '') {
    editor.value.chain().focus().extendMarkRange('link').unsetLink().run()

    return
  }

  // update link
  editor.value.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
}

watch(
  () => props.modelValue,
  (newValue) => {
    if (editor.value) {
      // Skip external updates while the editor is focused — the user's active typing
      // takes priority. Without this guard, a config three-way merge or debounced
      // save response can overwrite option.name, triggering setContent() which wipes
      // in-progress keystrokes and snaps the cursor position. (ENG-966)
      if (editor.value.isFocused) return

      const currentHtml = getSerializedHTML(editor.value)
      const currentLiquid = props.raw ? currentHtml : htmlToLiquid(currentHtml)
      if (newValue !== currentLiquid) {
        editor.value.commands.setContent(props.raw ? newValue : liquidToHtml(newValue))
      }
    }
  }
)

// Watch for changes to the disabled prop
watch(
  () => props.disabled,
  (newValue) => {
    if (editor.value) {
      editor.value.setEditable(!newValue)
    }
  }
)

const personalizeDialogVisible = ref(false)
const conditionalEditorVisible = ref(false)
const selectedText = ref('')
const selectedConditional = ref(null)
const showLiquidVariablesPicker = ref(false)

// Check if we're in a Flow context (editing offers/messages in a flow)
const route = useRoute()
const isInFlowContext = computed(() => {
  return route.params.offerFunnelId !== undefined
})

// Get available Liquid variables for the flow
const { hasLiquidVariables } = useFlowLiquidVariables()

// Check if there are any variables available (flow variables OR ESP integration)
const hasAnyVariables = computed(() => {
  return hasLiquidVariables.value || !!integratedPartner.value
})

const promptPersonalizer = () => {
  selectedText.value =
    editor.value.state.doc.textBetween(editor.value.state.selection.from, editor.value.state.selection.to, '') || ''
  personalizeDialogVisible.value = true
}

const promptConditional = () => {
  selectedText.value =
    editor.value.state.doc.textBetween(editor.value.state.selection.from, editor.value.state.selection.to, '') || ''
  selectedConditional.value = null // Reset the selected conditional state
  conditionalEditorVisible.value = true
}

const insertNewPersonalizationPhrase = (phrase) => {
  editor.value.commands.setPersonalizer(phrase.id)
  personalizeDialogVisible.value = false
}

const insertNewConditional = (conditional) => {
  if (selectedConditional.value && selectedConditional.value.conditionalId === conditional.conditionalId) {
    // Update existing conditional
    // Preserve full HTML for serializer; also keep a short preview for the chip
    const fullElseHtml = conditional.elseContent || ''
    const textContent = stripHtml(fullElseHtml).replace(/\s+/g, ' ').trim()
    const firstSentence = textContent.match(/^[^.!?]{1,80}[.!?]?/)
    const preview = (firstSentence ? firstSentence[0] : textContent.substring(0, 80)).trim()
    editor.value.commands.updateLiquidConditional({
      ...conditional,
      elseHtml: fullElseHtml,
      elseContent: preview
    })
  } else {
    // Insert new conditional
    const fullElseHtml = conditional.elseContent || ''
    const textContent = stripHtml(fullElseHtml).replace(/\s+/g, ' ').trim()
    const firstSentence = textContent.match(/^[^.!?]{1,80}[.!?]?/)
    const preview = (firstSentence ? firstSentence[0] : textContent.substring(0, 80)).trim()
    editor.value.commands.setLiquidConditional({
      ...conditional,
      elseHtml: fullElseHtml,
      elseContent: preview
    })
  }

  conditionalEditorVisible.value = false
  selectedConditional.value = null
}

const removeConditional = (conditional) => {
  // Replace the conditional with its else content
  editor.value.commands.replaceLiquidConditional(conditional.conditionalId, conditional.elseContent)
  selectedConditional.value = null
}

const aiPersonalizeVisible = ref(false)

const promptAi = () => {
  const { from, to } = editor.value.state.selection
  // selectedText.value =
  //   editor.value.state.doc.textBetween(
  //     editor.value.state.selection.from,
  //     editor.value.state.selection.to,
  //     ''
  //   ) || ''
  selectedText.value = getHTMLFromFragment(
    editor.value.state.doc.slice(editor.value.state.selection.from, editor.value.state.selection.to).content,
    editor.value.schema
  )
  const surroundingContent = editor.value.getHTML()
  aiPersonalizeVisible.value = true
  editor.value.emit('aiPersonalize', { selectedText: selectedText.value, surroundingContent })
}

const insertPersonalizedContent = (content) => {
  // Get the current selection
  const { from, to } = editor.value.state.selection

  // We need to handle this differently to avoid creating new paragraphs
  // The issue is that liquidToHtml wraps content in <p> tags if it doesn't start with block elements

  // First, let's process the content but avoid the automatic paragraph wrapping
  // We'll create the HTML spans for the liquid conditionals without wrapping in <p>
  const processedContent = liquidToHtml(content)

  // Remove any wrapping <p> tags that liquidToHtml might have added
  const unwrappedContent = processedContent.replace(/^<p>/, '').replace(/<\/p>$/, '')

  // Now insert the content as inline HTML
  editor.value
    .chain()
    .focus()
    .deleteRange({ from, to }) // Delete the selection first
    .insertContentAt(from, unwrappedContent, {
      parseOptions: {
        preserveWhitespace: 'full'
      }
    })
    .run()
}

const insertLiquidVariable = (liquidTag) => {
  // Get the current cursor position
  const { from } = editor.value.state.selection

  // Insert the Liquid tag at the cursor position
  editor.value.chain().focus().insertContentAt(from, liquidTag).run()
}

defineExpose({
  editor
})
</script>

<style lang="scss">
/* Basic editor styles */
.tiptap {
  :first-child {
    margin-top: 0;
  }

  /* List styles */
  ul,
  ol {
    padding: 0 1rem;
    margin: 1.25rem 1rem 1.25rem 0.4rem;

    li p {
      margin-top: 0.25em;
      margin-bottom: 0.25em;
    }
  }

  /* Heading styles */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    line-height: 1.1;
    text-wrap: pretty;
  }

  h1,
  h2 {
    margin-top: 1rem;
    margin-bottom: 0.5rem;
  }

  h1 {
    font-size: 1.4rem;
  }

  h2 {
    font-size: 1.2rem;
  }

  h3 {
    font-size: 1.1rem;
  }

  h4,
  h5,
  h6 {
    font-size: 1rem;
  }

  /* Code and preformatted text styles */
  code {
    background-color: var(--purple-light);
    border-radius: 0.4rem;
    color: var(--black);
    font-size: 0.85rem;
    padding: 0.25em 0.3em;
  }

  pre {
    background: var(--black);
    border-radius: 0.5rem;
    color: var(--white);
    font-family: 'JetBrainsMono', monospace;
    margin: 1.5rem 0;
    padding: 0.75rem 1rem;

    code {
      background: none;
      color: inherit;
      font-size: 0.8rem;
      padding: 0;
    }
  }

  blockquote {
    border-left: 3px solid var(--gray-3);
    margin: 1.5rem 0;
    padding-left: 1rem;
  }

  hr {
    border: none;
    border-top: 1px solid var(--gray-2);
    margin: 2rem 0;
  }

  p {
    margin-top: 0.75rem;
  }

  /* If a paragraph contains only a block conditional chip, collapse its own margins
     so spacing is controlled by the chip and/or the following block. This prevents
     perceived "extra line breaks" around chip-only paragraphs. */
  p:has(> span.conditional-chip-block) {
    margin-top: 0; /* chip controls top spacing */
    margin-bottom: 0; /* headings/next paragraphs control following spacing */
  }

  a {
    @apply text-blue-500 underline;
  }

  p:first-child {
    margin: 0;
  }
}

.floating-menu {
  display: flex;
  background-color: var(--gray-3);
  padding: 0.1rem;
  border-radius: 0.5rem;

  button {
    background-color: unset;
    padding: 0.275rem 0.425rem;
    border-radius: 0.3rem;

    &:hover {
      background-color: var(--gray-3);
    }

    &.is-active {
      background-color: var(--white);
      color: var(--purple);

      &:hover {
        color: var(--purple-contrast);
      }
    }
  }
}

/* Bubble menu */
// .bubble-menu {
//   background-color: #ffffff;
//   border: 1px solid var(--gray-1);
//   border-radius: 0.7rem;
//   box-shadow: var(--shadow);
//   display: flex;
//   padding: 0.2rem;

//   button {
//     background-color: unset;

//     &:hover {
//       background-color: var(--gray-3);
//     }

//     &.is-active {
//       background-color: var(--purple);

//       &:hover {
//         background-color: var(--purple-contrast);
//       }
//     }
//   }
// }
</style>
